import { partyApi } from "../api/partyAPi";
import { getCurrentUser } from "../auth/auth.service";
import type { PartyItem } from "../types/party.types";

/** How many parties a picker offers before the user types to narrow it. */
export const PARTY_PICKER_PAGE_SIZE = 50;

export interface PartyPickerQuery {
  entityTypeNames?: string[];
  /** 1 = Entity, 2 = Individual. */
  partyTypes?: number[];
  /** What the user typed into the picker's search box. */
  searchText?: string;
  /**
   * Parties that must be in the result whatever the search — the one the
   * field already holds, so its label still resolves when it is not among
   * the most recent matches.
   */
  includeIds?: (string | null | undefined)[];
  pageSize?: number;
  /** For pickers that page as they scroll (Autocomplete). */
  pageIndex?: number;
  /** Offer active parties only (the default). */
  activeOnly?: boolean;
}

/**
 * Party options for a picker, searched on the server.
 *
 * Pickers used to load up to 1000 parties through the Party list's redux
 * thunk: every party's full record plus the list's filter facets on each
 * open, and — because the thunk writes the shared list state — they reset
 * the Parties page behind the user's back, or came back empty when that
 * page happened to be loading. This asks only for the most recent
 * matches (active, not deleted) and leaves the list state alone; the
 * picker's search box narrows it further through `searchText`.
 */
export async function searchPartyOptions({
  entityTypeNames = [],
  partyTypes = [],
  searchText = "",
  includeIds = [],
  pageSize = PARTY_PICKER_PAGE_SIZE,
  pageIndex = 0,
  activeOnly = true,
}: PartyPickerQuery): Promise<PartyItem[]> {
  return (
    await searchPartyPage({
      entityTypeNames,
      partyTypes,
      searchText,
      includeIds,
      pageSize,
      pageIndex,
      activeOnly,
    })
  ).rows;
}

/** As searchPartyOptions, with the total match count for paging. */
export async function searchPartyPage({
  entityTypeNames = [],
  partyTypes = [],
  searchText = "",
  includeIds = [],
  pageSize = PARTY_PICKER_PAGE_SIZE,
  pageIndex = 0,
  activeOnly = true,
}: PartyPickerQuery): Promise<{ rows: PartyItem[]; count: number }> {
  const user = await getCurrentUser();
  const base = {
    orderBy: "modificationdate",
    sortAscending: false,
    includeFacets: false,
    groupIds: [`${user.companyId}`],
    includeDeleted: false,
    registeredNames: [],
    emails: [],
    myMode: true,
  };

  // Pinned parties ride along with the first page only.
  const wanted =
    pageIndex === 0
      ? [...new Set(includeIds.filter((id): id is string => !!id))]
      : [];
  const [page, pinned] = await Promise.all([
    partyApi.searchParty({
      ...base,
      pageIndex,
      pageSize,
      ids: [],
      entityTypeNames,
      partyTypes,
      isActive: activeOnly ? true : null,
      searchText: searchText.trim(),
    }),
    wanted.length
      ? partyApi.searchParty({
          ...base,
          pageIndex: 0,
          pageSize: wanted.length,
          ids: wanted,
          entityTypeNames: [],
          partyTypes: [],
          isActive: null,
          searchText: "",
        })
      : Promise.resolve({ data: [] as PartyItem[] }),
  ]);

  const rows: PartyItem[] = [...(page?.data || [])];
  const seen = new Set(rows.map((p) => p.id));
  for (const p of pinned?.data || []) {
    if (!seen.has(p.id)) rows.push(p);
  }
  return { rows, count: Number(page?.count ?? rows.length) };
}

/**
 * One party's full record, for filling a form from the party just picked.
 * A direct search rather than the Party list's thunk, which would overwrite
 * that list's state (and return nothing while it is loading).
 */
export async function getPartyById(id: string): Promise<PartyItem | null> {
  if (!id) return null;
  const user = await getCurrentUser();
  const res = await partyApi.searchParty({
    pageIndex: 0,
    pageSize: 1,
    orderBy: "",
    sortAscending: false,
    includeFacets: false,
    groupIds: [`${user.companyId}`],
    ids: [id],
    includeDeleted: false,
    entityTypeNames: [],
    partyTypes: [],
    registeredNames: [],
    emails: [],
    isActive: null,
    myMode: true,
    searchText: "",
  });
  return res?.data?.[0] ?? null;
}
